import Pusher from 'pusher-js';
import Echo, { Channel } from 'laravel-echo';
import {
  Observable,
  ReplaySubject,
  Subject,
  combineLatest,
  delay,
  distinctUntilChanged,
  first,
  map,
  of,
  startWith,
  switchMap,
} from 'rxjs';
import { getCustomerStore } from 'stores/customer.store';
import { tokens } from './tokens';
import { $get, $getWithMeta, $post } from './api';
import { v4 as uuidV4 } from 'uuid';
import { convertKeysToCamel, JsonValue } from 'utils/caseConverter';

(window as any).Pusher = Pusher;

const CUSTOMER_CHANNEL_PREFIX = 'ConnectionEngine.Modules.Core.Entities.Customer.';

class PusherService {
  private $customerChannel = new ReplaySubject<Channel>();

  private subjects: {
    [key in string]: Subject<any>;
  } = {};

  constructor() {
    combineLatest([
      getCustomerStore().$customer.pipe(
        map((c) => c?.id),
        distinctUntilChanged()
      ),
      tokens.$accessToken,
    ]).subscribe(([customerId, token]) => {
      if (customerId && token) {
        this.$customerChannel.next(
          new Echo({
            broadcaster: 'pusher',
            key: import.meta.env.REACT_APP_PUSHER_KEY,
            cluster: import.meta.env.REACT_APP_PUSHER_APP_CLUSTER,
            encrypted: true,
            auth: {
              headers: {
                Authorization: 'Bearer ' + token,
              },
            },
          }).private(`${CUSTOMER_CHANNEL_PREFIX}${customerId}`)
        );
      }
    });
  }

  public $listen = <T extends object = {}>(event: string): Observable<T> => {
    if (this.subjects[event]) return this.subjects[event].asObservable();
    const $s = new Subject<T>();
    this.subjects[event] = $s;
    this.$customerChannel.pipe(first()).subscribe((customerChannel) => {
      customerChannel.listen(event, (data: T) => {
        const formattedData = convertKeysToCamel(data as unknown as JsonValue) as T;
        $s.next(formattedData);
      });
    });
    return $s.asObservable() as Observable<T>;
  };

  public stopListening = (event: string) => {
    if (this.subjects[event]) {
      this.subjects[event].unsubscribe();
      this.subjects[event].complete();
      delete this.subjects[event];
    }
    this.$customerChannel.pipe(first()).subscribe((customerChannel) => customerChannel.stopListening(event));
  };

  public makeAsyncCall = {
    $post: <T extends {}>(options: {
      url: Parameters<typeof $post>[0];
      listenEvent: string;
      first: (event: T) => boolean;
      body?: Parameters<typeof $post>[1];
      options?: Parameters<typeof $get>[2];
    }) => {
      return combineLatest([
        this.$listen<T>(options.listenEvent),
        $post(options.url, options.body, options.options),
      ]).pipe(
        first(([event]) => options.first(event)),
        map(([event]) => event)
      );
    },
  };

  public $getAndListen = <T extends {}>(options: {
    getUrl: Parameters<typeof $get>[0];
    listenEvent: string;
    failureEvent?: string;
    getParams?: Parameters<typeof $get>[1];
    getOptions?: Parameters<typeof $get>[2];
    requestId?: string;
    resultHasMeta?: boolean;
    requestDelay?: number;
  }) => {
    const requestId = options.requestId || uuidV4();
    return combineLatest([
      this.$listen<{ requestId: string; readUrl: string }>(options.listenEvent).pipe(startWith(null)),
      of(null).pipe(
        delay(options.requestDelay || 0),
        switchMap(() => $get(options.getUrl, { ...options.getParams, requestId }, options.getOptions))
      ),
      ...(!!options.failureEvent
        ? [
            this.$listen<{ requestId: string; error: string }>(options.failureEvent).pipe(
              map((event) => {
                throw event;
              }),
              startWith(null)
            ),
          ]
        : []),
    ]).pipe(
      first(([event]) => event?.requestId === requestId),
      switchMap(([event]) =>
        $get<T>(
          event?.readUrl.split('/api/')[1] || '',
          {},
          { useSnakeCase: true, ...(options.resultHasMeta && { hasMeta: true }) }
        )
      )
    );
  };

  public $getWithMetaAndListen = <
    T extends {},
    M = { first: number; total: number; currentPage: number; from: number; perPage: number }
  >(options: {
    getUrl: Parameters<typeof $get>[0];
    listenEvent: string;
    failureEvent?: string;
    getParams?: Parameters<typeof $get>[1];
    getOptions?: Parameters<typeof $get>[2];
    requestId?: string;
    requestDelay?: number;
  }) => {
    const requestId = options.requestId || uuidV4();
    return combineLatest([
      this.$listen<{ requestId: string; readUrl: string }>(options.listenEvent).pipe(startWith(null)),
      of(null).pipe(
        delay(options.requestDelay || 0),
        switchMap(() => $get(options.getUrl, { ...options.getParams, requestId }, options.getOptions))
      ),
      ...(!!options.failureEvent
        ? [
            this.$listen<{ requestId: string; error: string }>(options.failureEvent).pipe(
              map((event) => {
                throw event;
              }),
              startWith(null)
            ),
          ]
        : []),
    ]).pipe(
      first(([event]) => event?.requestId === requestId),
      switchMap(([event]) =>
        $getWithMeta<T, M>(event?.readUrl.split('/api/')[1] || '', {}, { useSnakeCase: true })
      )
    );
  };
}

const pusher = new PusherService();

export { pusher };
